import type { JSX } from 'react';
import { Pencil, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import type { Education } from '../types/education.types';
import { EDUCATION_LEVEL_LABELS } from '../types/education.types';

export interface EducationItemProps {
  education: Education;
  onEdit: (education: Education) => void;
  onDelete: (educationId: string) => void;
}

function formatYearRange(yearBegin: number, yearEnd?: number | null): string {
  return yearEnd ? `${yearBegin} - ${yearEnd}` : `${yearBegin} - Present`;
}

export function EducationItem({ education, onEdit, onDelete }: EducationItemProps): JSX.Element {
  return (
    <div
      className="flex items-start justify-between p-3 rounded-lg border"
      data-testid="education-item"
    >
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <span className="font-medium text-sm">{education.institution}</span>
          <Badge variant="secondary" className="text-xs">
            {EDUCATION_LEVEL_LABELS[education.educationLevel]}
          </Badge>
        </div>
        <p className="text-sm text-muted-foreground">{education.educationField}</p>
        <p className="text-xs text-muted-foreground">
          {education.city}, {education.country} &middot;{' '}
          {formatYearRange(education.yearBegin, education.yearEnd)}
        </p>
      </div>
      <div className="flex gap-1">
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          aria-label="Edit education"
          onClick={() => onEdit(education)}
        >
          <Pencil className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-destructive"
          aria-label="Delete education"
          onClick={() => onDelete(education.id)}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
